<template>
  <FormCarousel ref="carouselControls" hide-progress>
    <FormCarouselItem
      name="signUp"
      :label="t('onboarding.signup.signup.title')"
      :next-loading="submitting"
      :auto-next="false"
      @next="submit"
    >
      <p class="text-h6 mb-10">
        {{ t("onboarding.signup.signup.subtitle") }}
      </p>
      <div class="d-flex flex-column ga-2">
        <y-text-field
          v-model="signUpForm.email"
          :label="t('onboarding.signup.signup.email_label')"
          :error-messages="validationErrors(v$.email.$errors)"
          :disabled="isEmailDisabled"
        >
          <template #append-inner>
            <v-icon style="pointer-events: auto" @click="toggleEmailDisabled">
              mdi-pencil-circle-outline
            </v-icon>
          </template>
        </y-text-field>
        <y-text-field
          v-model="signUpForm.password"
          :label="t('onboarding.signup.signup.password_label')"
          :error-messages="validationErrors(v$.password.$errors)"
          :append-inner-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
          :type="showPassword ? 'text' : 'password'"
          :hint="t('onboarding.signup.signup.password_hint')"
          persistent-hint
          @click:append-inner="showPassword = !showPassword"
        />
        <y-text-field
          v-model="signUpForm.confirmPassword"
          :label="t('onboarding.signup.signup.confirm_password_label')"
          :error-messages="validationErrors(v$.confirmPassword.$errors)"
          :append-inner-icon="showConfirmPassword ? 'mdi-eye' : 'mdi-eye-off'"
          :type="showConfirmPassword ? 'text' : 'password'"
          @click:append-inner="showConfirmPassword = !showConfirmPassword"
        />
      </div>
    </FormCarouselItem>
    <FormCarouselItem
      name="signUpConfirm"
      :label="t('onboarding.signup.signup_confirm.title')"
      :next-loading="submitting"
      :auto-next="false"
      no-back-button
      @next="signUpConfirm"
    >
      <p class="text-h6 mb-10">
        {{ t("onboarding.signup.signup_confirm.subtitle") }}
      </p>
      <y-text-field
        v-model="signUpForm.confirmationCode"
        :label="t('onboarding.signup.signup_confirm.confirmation_code_label')"
        :error-messages="
          (v$.confirmationCode.$dirty &&
            v$.confirmationCode.$invalid &&
            v$.confirmationCode.$silentErrors[0].$message.toString()) ||
          ''
        "
        @input="authStore.resetErrorMessages"
      />
      <div class="d-flex flex-column ga-5 py-3">
        <a class="font-weight-bold text-purple-4 cursor-pointer" @click="resendEmail">{{
          t("onboarding.signup.signup_confirm.resend_email_btn")
        }}</a>
        <a class="font-weight-bold text-purple-4 cursor-pointer" @click="carouselControls?.prev">
          {{ t("onboarding.signup.signup_confirm.change_email_btn") }}
        </a>
      </div>
    </FormCarouselItem>
    <FormCarouselItem
      name="signInConfirm"
      :label="t('onboarding.signup.signin_confirm.title')"
      :next-loading="submitting"
      no-back-button
      @next="signInConfirm"
    >
      <div class="d-flex flex-column ga-2">
        <y-text-field
          v-model="signUpForm.password"
          :label="t('onboarding.signup.signin_confirm.password_label')"
          :error-messages="validationErrors(v$.password.$errors)"
          :append-inner-icon="showPasswordReentry ? 'mdi-eye' : 'mdi-eye-off'"
          :type="showPasswordReentry ? 'text' : 'password'"
          persistent-hint
          @click:append-inner="showPassword = !showPassword"
        />
      </div>
    </FormCarouselItem>
  </FormCarousel>
</template>

<script setup lang="ts">
import useVuelidate from "@vuelidate/core";
import { email } from "@vuelidate/validators";
import { computed, onMounted, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import FormCarousel from "@/components/Form/FormCarousel.vue";
import { useApplicationStore } from "@/store/application";
import { useAuthenticationStore } from "@/store/authentication";
import { useOnboardingStore } from "@/store/onboarding";
import { i18nRequired, passwordValidations, withI18nMessage } from "@/utils/custom-validators";
import { validationErrors } from "@/utils/format";

const router = useRouter();
const carouselControls = ref<InstanceType<typeof FormCarousel>>();
const onboardingStore = useOnboardingStore();
const authStore = useAuthenticationStore();
const applicationStore = useApplicationStore();
const { t } = useI18n();
const submitting = ref(false);
const showPassword = ref(false);
const showConfirmPassword = ref(false);
const showPasswordReentry = ref(false);
const isEmailDisabled = ref(true);

const route = useRoute();
const inviteToken = route.query?.invite as string | undefined;

const signUpForm = reactive({
  email: onboardingStore.draftMerchant?.email || "",
  password: "",
  confirmPassword: "",
  confirmationCode: "",
});

const toggleEmailDisabled = () => {
  isEmailDisabled.value = !isEmailDisabled.value;
};

onMounted(() => {
  if (onboardingStore.draftMerchant?.userStatus === "UNCONFIRMED") {
    carouselControls.value?.next();
  }
});

const rules = computed(() => ({
  email: {
    $autoDirty: true,
    required: i18nRequired,
    email: withI18nMessage("form.emailInvalid", email),
  },
  password: {
    $autoDirty: true,
    ...passwordValidations,
  },
  confirmPassword: {
    $autoDirty: true,
    ...passwordValidations,
  },
  confirmationCode: {
    $autoDirty: true,
    required: i18nRequired,
  },
}));

const v$ = useVuelidate(rules, signUpForm);

const submit = async () => {
  carouselControls.value?.clearError();
  v$.value.$touch();
  if (v$.value.password.$invalid || v$.value.confirmPassword.$invalid) {
    carouselControls.value?.setError(
      v$.value.$errors[0]?.$message.toString() || t("onboarding.signup.generic_signup_error"),
    );
    return;
  }
  try {
    submitting.value = true;
    let emailLower = onboardingStore?.draftMerchant?.email.toLowerCase() || "";
    const phoneNumberFull = onboardingStore.draftMerchant?.phoneNumber;
    if (!phoneNumberFull) {
      carouselControls.value?.setError(t("form.phoneNumberInvalid"));
      return;
    }

    let userId = onboardingStore.draftMerchant?.userId;
    if (emailLower !== signUpForm.email.toLowerCase() || inviteToken) {
      const draftMerchant = await onboardingStore.updateEmail(
        signUpForm.email.toLowerCase(),
        inviteToken,
      );
      emailLower = signUpForm.email.toLowerCase();
      userId = draftMerchant.userId;
    }

    const signUpSuccess = await authStore.initSignUp({
      email: emailLower,
      password: signUpForm.password,
      phoneNumber: phoneNumberFull,
      userId,
    });
    if (signUpSuccess) {
      if (authStore.signUp.userRequiresConfirmation) {
        carouselControls.value?.next();
      }
    } else {
      carouselControls.value?.setError(authStore.signUp.errorMessage);
    }
  } catch (error) {
    console.error("error during onboarding signup", error);
    carouselControls.value?.setError(t("onboarding.signup.generic_signup_error"));
  } finally {
    submitting.value = false;
  }
};

const signUpConfirm = async () => {
  v$.value.$touch();
  if (v$.value.confirmationCode.$invalid) {
    carouselControls.value?.setError(
      v$.value.$errors[0]?.$message.toString() || t("onboarding.signup.generic_signup_error"),
    );
    return;
  }
  submitting.value = true;
  const emailLower = onboardingStore?.draftMerchant?.email.toLowerCase() || "";
  const res = await authStore.initConfirmUser({
    email: emailLower,
    confirmationCode: signUpForm.confirmationCode,
  });
  if (!res) {
    carouselControls.value?.setError(authStore.confirmUser.errorMessage);
  } else {
    if (signUpForm.password) {
      signInConfirm();
    } else {
      carouselControls.value?.next();
      submitting.value = false;
    }
  }
};

const signInConfirm = async () => {
  submitting.value = true;
  const emailLower = onboardingStore?.draftMerchant?.email.toLowerCase() || "";
  try {
    const resSignin = await authStore.initSignIn({
      email: emailLower,
      password: signUpForm.password,
    });
    if (!resSignin) {
      carouselControls.value?.setError(authStore.signIn.errorMessage);
    } else {
      router.push("/");
      onboardingStore.clearSavedDraftMerchant();
    }
  } catch (error) {
    console.error("error during onboarding signup", error);
    carouselControls.value?.setError(t("onboarding.signup.generic_signup_error"));
  } finally {
    submitting.value = false;
  }
};

const resendEmail = async () => {
  const emailLower = onboardingStore?.draftMerchant?.email.toLowerCase() || "";
  const resendSuccess = await authStore.initResendConfirmationCode(emailLower);
  if (resendSuccess) {
    applicationStore.notifyUser({
      message: t("onboarding.signup.signup_confirm.resend_email_success"),
    });
    return;
  }
};
</script>
